/**
 * Centralises all keyboard / mouse / pointer-lock handling and exposes it as a
 * simple, query-able state plus a few observer hooks. Game code never touches
 * the DOM event API directly — it asks the Input object.
 */
export class Input {
  private readonly keys = new Set<string>();
  private mouseDX = 0;
  private mouseDY = 0;
  private locked = false;

  private readonly pointerDownListeners: ((button: number) => void)[] = [];
  private readonly pointerUpListeners: ((button: number) => void)[] = [];
  private readonly lockChangeListeners: ((locked: boolean) => void)[] = [];

  constructor(private readonly canvas: HTMLCanvasElement) {
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('mousemove', this.onMouseMove);
    canvas.addEventListener('mousedown', this.onMouseDown);
    canvas.addEventListener('mouseup', this.onMouseUp);
    canvas.addEventListener('contextmenu', (e) => e.preventDefault());
    document.addEventListener('pointerlockchange', this.handleLockChange);
  }

  isDown(code: string): boolean {
    return this.keys.has(code);
  }

  get isPointerLocked(): boolean {
    return this.locked;
  }

  /** Read and clear the accumulated mouse movement for this frame. */
  consumeMouseDelta(): { x: number; y: number } {
    const delta = { x: this.mouseDX, y: this.mouseDY };
    this.mouseDX = 0;
    this.mouseDY = 0;
    return delta;
  }

  requestPointerLock(): void {
    this.canvas.requestPointerLock();
  }

  // --- synthetic input, driven by touch controls -------------------------
  // These let an alternative input source (TouchControls) feed the exact same
  // state the rest of the game already reads, so no game system needs to know
  // whether it is being driven by a keyboard/mouse or by a touchscreen.

  /** Press or release a virtual key (e.g. 'KeyW', 'Space', 'ShiftLeft'). */
  setKey(code: string, pressed: boolean): void {
    if (pressed) this.keys.add(code);
    else this.keys.delete(code);
  }

  /** Feed look movement as if the mouse had moved by (dx, dy) pixels. */
  addLookDelta(dx: number, dy: number): void {
    this.mouseDX += dx;
    this.mouseDY += dy;
  }

  /** Emit a synthetic pointer press/release (button 0 = left, 2 = right). */
  firePointerDown(button: number): void {
    this.pointerDownListeners.forEach((l) => l(button));
  }
  firePointerUp(button: number): void {
    this.pointerUpListeners.forEach((l) => l(button));
  }

  /** Begin a session without real pointer lock (touch devices). */
  activateTouchSession(): void {
    this.locked = true;
    this.lockChangeListeners.forEach((l) => l(true));
  }

  onPointerDown(listener: (button: number) => void): void {
    this.pointerDownListeners.push(listener);
  }
  onPointerUp(listener: (button: number) => void): void {
    this.pointerUpListeners.push(listener);
  }
  onLockChange(listener: (locked: boolean) => void): void {
    this.lockChangeListeners.push(listener);
  }

  private onKeyDown = (e: KeyboardEvent): void => {
    this.keys.add(e.code);
    if (e.code === 'Space') e.preventDefault();
  };
  private onKeyUp = (e: KeyboardEvent): void => {
    this.keys.delete(e.code);
  };
  private onMouseMove = (e: MouseEvent): void => {
    if (!this.locked) return;
    this.mouseDX += e.movementX;
    this.mouseDY += e.movementY;
  };
  private onMouseDown = (e: MouseEvent): void => {
    if (!this.locked) return;
    e.preventDefault();
    this.pointerDownListeners.forEach((l) => l(e.button));
  };
  private onMouseUp = (e: MouseEvent): void => {
    this.pointerUpListeners.forEach((l) => l(e.button));
  };
  private handleLockChange = (): void => {
    this.locked = document.pointerLockElement === this.canvas;
    this.lockChangeListeners.forEach((l) => l(this.locked));
  };
}
